'use client'

import { useCallback, useEffect, useState } from 'react'
import { IconHeart, IconHeartFilled } from '@tabler/icons-react'
import clsx from 'clsx'
import debounce from 'lodash.debounce'
import { useSession } from 'next-auth/react'

import { reportSentryError } from 'lib/sentry'
import {
  CustomerTypes,
  extractCustomerFromClientSession,
  extractTokenFromClientSession
} from 'modules/customer'
import { SeoListName } from 'shared/analytics'
import toast from 'shared/ui/utils/toast'
import { getApiErrors } from 'shared/utils/errors'

import { addToWishlistSeo } from '../../analytics'
import {
  addProductToFavourites,
  removeProductFromFavourites
} from '../../api/products'
import { useCartCouponCode } from '../../ports'
import { ProductTypes } from '../../types/products'

import styles from './AddToFavouritesButton.module.scss'

export interface AddToFavouritesButtonProps
  extends React.HTMLAttributes<HTMLButtonElement> {
  product: ProductTypes
  idxOnList?: number
  id?: string
  listName: SeoListName
  isInCart?: boolean
  seoButtonPlace: string
}

const AddToFavouritesButton = ({
  product,
  idxOnList,
  id,
  listName,
  isInCart,
  seoButtonPlace,
  className,
  ...props
}: AddToFavouritesButtonProps) => {
  const [checked, setChecked] = useState(false)
  const session = useSession()
  const token = extractTokenFromClientSession(session)

  const cartCouponCode = useCartCouponCode()

  const user: CustomerTypes | undefined = extractCustomerFromClientSession(session)

  const debouncedTrackEvent = useCallback(
    debounce((productItem: ProductTypes) => {
      addToWishlistSeo({
        user,
        product: productItem,
        button_place: seoButtonPlace,
        idxOnList: idxOnList || 0,
        coupon: cartCouponCode,
        listName
      })
    }, 300),
    [user, seoButtonPlace, idxOnList, listName]
  )

  const toggleFavourites = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    e.preventDefault()

    if (!token) {
      toast.error('Zaloguj się aby dodać produkt do ulubionych')

      return
    }

    if (!checked) {
      addProductToFavourites({
        id: product.id,
        token
      })
        .then(() => {
          toast.success('Dodano produkt do ulubionych')
          setChecked(prevCheck => !prevCheck)
          debouncedTrackEvent(product)
        })
        .catch(err => {
          toast.error(getApiErrors(err))
          reportSentryError(err, 'Failed to add product to favourites')
        })
    } else {
      removeProductFromFavourites({
        id: product.id,
        token
      })
        .then(() => {
          toast.success('Usunięto produkt z ulubionych')
          setChecked(prevCheck => !prevCheck)
        })
        .catch(err => {
          toast.error(getApiErrors(err))
          reportSentryError(err, 'Failed to remove product from favourites')
        })
    }
  }

  useEffect(() => {
    if (product) {
      setChecked(product.isFavourite)
    }
  }, [product])

  return (
    <button
      className={clsx(
        styles.wrapper,
        { [styles.inCartUnchecked]: !checked && isInCart },
        className
      )}
      id={id}
      onClick={toggleFavourites}
      aria-label={checked ? 'Usuń z ulubionych' : 'Dodaj do ulubionych'}
      {...props}
    >
      {checked ? (
        <IconHeartFilled height={18} width={18} />
      ) : (
        <IconHeart height={18} width={18} />
      )}
    </button>
  )
}

export default AddToFavouritesButton
